import { useCallback, useEffect, useRef } from 'react';
import { PRODUCT_AREAS, trackProductArea } from '../utils/analytics';

/**
 * Record that the signed-in user opened a product area, and return a callback for
 * recording meaningful actions within it.
 *
 * The view event fires once per feature per browser session so that repeated
 * navigation between tools does not inflate the counts. Actions are always
 * recorded, since they are what distinguishes browsing from real usage.
 *
 * @param {string} feature - One of PRODUCT_AREAS
 * @returns {(action: string, options?: {schoolUuid?: string}) => void} trackAction
 */
export const useFeatureUsage = (feature) => {
  const viewTracked = useRef(false);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;

    const sessionKey = `featureViewed_${feature}`;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, 'true');
    } catch {
      // Private browsing or storage disabled — fall through and track the view
    }

    trackProductArea(feature, { eventType: 'view' });
  }, [feature]);

  return useCallback((action, { schoolUuid } = {}) => {
    trackProductArea(feature, { eventType: 'action', action, schoolUuid });
  }, [feature]);
};

export { PRODUCT_AREAS };
export default useFeatureUsage;
